import { Contest, Team, WorkoutActivity, ContestType, ActivityType, IndividualLeaderboardEntry, IndividualContestActivity } from '../models/types';
import { UserProfile } from '../types/user.types';
import config from '../config/env.config';

const API_BASE_URL = config.API_URL;

export type ImportSkipReason = 'missing_fields' | 'duplicate_email' | 'unknown_team' | 'unknown_user' | 'already_on_team';

export interface TeamMemberImportResult {
  applied: boolean;
  rowCount: number;
  teams: { teamId: string; teamName: string; currentMembers: number; adding: number }[];
  skipped: { line: number; email: string; teamName: string; reason: ImportSkipReason; currentTeam?: string }[];
  added: number;
  failed: { userId: string; teamId: string; reason: string }[];
}

// `strava` is null when the user has no Strava account linked; only already stored workouts were credited then.
export interface ContestActivitySyncResult {
  creditedCount: number;
  strava: { syncedCount: number; skippedCount: number; errors: string[]; stravaBusy: boolean } | null;
}

// Keeps the HTTP status so callers can tell "already running" (409) and "too many syncs" (429) apart.
export class ContestActivitySyncError extends Error {
  constructor(readonly status: number, message: string) {
    super(message);
  }
}

class ApiService {
  readonly API_BASE_URL = API_BASE_URL;

  getHeaders(): HeadersInit {
    // No need for Authorization header since tokens are in httpOnly cookies
    return {
      'Content-Type': 'application/json',
    };
  }

  getRequestOptions(): RequestInit {
    return {
      headers: this.getHeaders(),
      credentials: 'include', // Include httpOnly cookies
    };
  }

  async getContest(contestId: string): Promise<Contest> {
    const response = await fetch(`${API_BASE_URL}/contests/${contestId}`, {
      ...this.getRequestOptions(),
    });
    
    if (!response.ok) {
      throw new Error('Failed to fetch contest');
    }
    
    return response.json();
  }

  async getTeamsForContest(contestId: string): Promise<Team[]> {
    const response = await fetch(`${API_BASE_URL}/contests/${contestId}/teams`, {
      ...this.getRequestOptions(),
    });
    
    if (!response.ok) {
      throw new Error('Failed to fetch teams');
    }
    
    return response.json();
  }

  async getContests(): Promise<Contest[]> {
    const response = await fetch(`${this.API_BASE_URL}/contests`, {
      ...this.getRequestOptions(),
    });
    if (!response.ok) {
      throw new Error('Failed to fetch contests');
    }
    return response.json();
  }

  async createContest(contestData: {
    name: string;
    detail: string;
    startAt: string;
    endAt: string;
    groupId: string;
    contestType: ContestType;
    activityType: ActivityType;
    minPace?: number;
    maxPace?: number;
    minDistance?: number;
  }): Promise<Contest> {
    const response = await fetch(`${this.API_BASE_URL}/contests`, {
      method: 'POST',
      ...this.getRequestOptions(),
      body: JSON.stringify({
        name: contestData.name,
        detail: contestData.detail,
        startAt: new Date(contestData.startAt),
        endAt: new Date(contestData.endAt),
        groupId: contestData.groupId,
        contestType: contestData.contestType,
        activityType: contestData.activityType,
        minPace: contestData.minPace,
        maxPace: contestData.maxPace,
        minDistance: contestData.minDistance,
        numberOfParticipants: 0,
        numberOfTeams: 0,
        teamIds: []
      }),
    });

    if (!response.ok) {
      throw new Error('Failed to create contest');
    }

    return response.json();
  }

  async updateContest(contestId: string, contestData: {
    name: string;
    detail: string;
    startAt: string;
    endAt: string;
    contestType: ContestType;
    activityType: ActivityType;
    minPace?: number;
    maxPace?: number;
    minDistance?: number;
  }): Promise<Contest> {
    const response = await fetch(`${this.API_BASE_URL}/contests/${contestId}`, {
      method: 'PUT',
      ...this.getRequestOptions(),
      body: JSON.stringify({
        name: contestData.name,
        detail: contestData.detail,
        startAt: new Date(contestData.startAt),
        endAt: new Date(contestData.endAt),
        contestType: contestData.contestType,
        activityType: contestData.activityType,
        minPace: contestData.minPace,
        maxPace: contestData.maxPace,
        minDistance: contestData.minDistance
      }),
    });

    if (!response.ok) {
      throw new Error('Failed to update contest');
    }

    return response.json();
  }

  async deleteContest(contestId: string): Promise<void> {
    const response = await fetch(`${this.API_BASE_URL}/contests/${contestId}`, {
      method: 'DELETE',
      ...this.getRequestOptions(),
    });

    if (!response.ok) {
      throw new Error('Failed to delete contest');
    }
  }

  async createTeam(contestId: string, teamData: { name: string }): Promise<Team> {
    const response = await fetch(`${this.API_BASE_URL}/contests/${contestId}/teams`, {
      method: 'POST',
      ...this.getRequestOptions(),
      body: JSON.stringify(teamData),
    });

    if (!response.ok) {
      throw new Error('Failed to create team');
    }

    return response.json();
  }

  async addMemberToTeam(teamId: string, userId: string): Promise<void> {
    const response = await fetch(`${this.API_BASE_URL}/teams/${teamId}/members`, {
      method: 'POST',
      ...this.getRequestOptions(),
      body: JSON.stringify({ userId }),
    });

    if (!response.ok) {
      throw new Error('Failed to add member to team');
    }
  }

  async addMultipleMembersToTeam(teamId: string, userIds: string[]): Promise<{
    message: string;
    successful: string[];
    failed: { userId: string; reason: string }[];
    summary: { total: number; successful: number; failed: number };
  }> {
    const response = await fetch(`${this.API_BASE_URL}/teams/${teamId}/members/bulk`, {
      method: 'POST',
      ...this.getRequestOptions(),
      body: JSON.stringify({ userIds }),
    });

    if (!response.ok) {
      throw new Error('Failed to add multiple members to team');
    }

    return response.json();
  }

  // Assigns users to existing contest teams from `email,team` CSV text; dryRun only reports the plan.
  async importTeamMembers(contestId: string, csv: string, dryRun: boolean): Promise<TeamMemberImportResult> {
    const response = await fetch(`${this.API_BASE_URL}/contests/${contestId}/team-members/import${dryRun ? '?dryRun=true' : ''}`, {
      method: 'POST',
      ...this.getRequestOptions(),
      body: JSON.stringify({ csv }),
    });

    if (!response.ok) {
      const body = await response.json().catch(() => null);
      throw new Error(body?.message || 'Failed to import team members');
    }

    return response.json();
  }

  async removeMemberFromTeam(teamId: string, userId: string): Promise<void> {
    const response = await fetch(`${this.API_BASE_URL}/teams/${teamId}/members/${userId}`, {
      method: 'DELETE',
      ...this.getRequestOptions(),
    });

    if (!response.ok) {
      throw new Error('Failed to remove member from team');
    }
  }

  // Deletes a contest team before the contest starts; its members become unassigned.
  async deleteTeam(teamId: string): Promise<void> {
    const response = await fetch(`${this.API_BASE_URL}/teams/${teamId}`, {
      method: 'DELETE',
      ...this.getRequestOptions(),
    });

    if (!response.ok) {
      const body = await response.json().catch(() => null);
      throw new Error(body?.message || 'Failed to delete team');
    }
  }

  // Assigns, moves or (teamId null) unassigns a user within a team contest.
  async assignContestTeamMember(contestId: string, userId: string, teamId: string | null): Promise<void> {
    const response = await fetch(`${this.API_BASE_URL}/contests/${contestId}/team-members/${userId}`, {
      method: 'PUT',
      ...this.getRequestOptions(),
      body: JSON.stringify({ teamId }),
    });

    if (!response.ok) {
      const body = await response.json().catch(() => null);
      throw new Error(body?.message || 'Failed to assign team member');
    }
  }

  // Imports the caller's Strava activities since the contest start and credits the eligible ones to this contest.
  async syncContestActivities(contestId: string): Promise<ContestActivitySyncResult> {
    const response = await fetch(`${this.API_BASE_URL}/contests/${contestId}/sync-activities`, {
      method: 'POST',
      ...this.getRequestOptions(),
    });

    if (!response.ok) {
      const body = await response.json().catch(() => null);
      throw new ContestActivitySyncError(response.status, body?.message || 'Failed to sync activities');
    }

    return response.json();
  }

  async getTeamDetail(teamId: string): Promise<Team> {
    const response = await fetch(`${this.API_BASE_URL}/teams/${teamId}`, {
      ...this.getRequestOptions(),
    });

    if (!response.ok) {
      throw new Error('Failed to fetch team details');
    }

    return response.json();
  }

  async getTeamMemberActivities(teamId: string, userId: string): Promise<any[]> {
    const response = await fetch(`${this.API_BASE_URL}/teams/${teamId}/members/${userId}/activities`, {
      ...this.getRequestOptions(),
    });

    if (!response.ok) {
      throw new Error('Failed to fetch member activities');
    }

    return response.json();
  }

  async getUserActivities(): Promise<WorkoutActivity[]> {
    const response = await fetch(`${this.API_BASE_URL}/user/activities`, {
      ...this.getRequestOptions(),
    });

    if (!response.ok) {
      throw new Error('Failed to fetch user activities');
    }

    return response.json();
  }

  async getUserProfile(): Promise<UserProfile> {
    const response = await fetch(`${this.API_BASE_URL}/user/profile`, {
      ...this.getRequestOptions(),
    });

    if (!response.ok) {
      throw new Error('Failed to fetch user profile');
    }

    return response.json();
  }

  async getIndividualContestLeaderboard(contestId: string): Promise<IndividualLeaderboardEntry[]> {
    const response = await fetch(`${this.API_BASE_URL}/contests/${contestId}/leaderboard`, {
      ...this.getRequestOptions(),
    });

    if (!response.ok) {
      throw new Error('Failed to fetch individual contest leaderboard');
    }

    return response.json();
  }


  async getIndividualContestActivities(contestId: string, userId: string): Promise<IndividualContestActivity[]> {
    const response = await fetch(`${this.API_BASE_URL}/contests/${contestId}/users/${userId}/activities`, {
      ...this.getRequestOptions(),
    });

    if (!response.ok) {
      throw new Error('Failed to fetch individual contest activities');
    }

    return response.json();
  }

  async addParticipantToContest(contestId: string, participantId: string): Promise<void> {
    const response = await fetch(`${this.API_BASE_URL}/contests/${contestId}/participants`, {
      method: 'POST',
      ...this.getRequestOptions(),
      body: JSON.stringify({ participantId }),
    });

    if (!response.ok) {
      throw new Error('Failed to add participant to contest');
    }
  }

  async removeParticipantFromContest(contestId: string, participantId: string): Promise<void> {
    const response = await fetch(`${this.API_BASE_URL}/contests/${contestId}/participants/${participantId}`, {
      method: 'DELETE',
      ...this.getRequestOptions(),
    });

    if (!response.ok) {
      throw new Error('Failed to remove participant from contest');
    }
  }

  async getContestParticipants(contestId: string): Promise<any[]> {
    const response = await fetch(`${this.API_BASE_URL}/contests/${contestId}/participants`, {
      ...this.getRequestOptions(),
    });

    if (!response.ok) {
      throw new Error('Failed to fetch contest participants');
    }

    return response.json();
  }

  async getAvailableContestParticipants(contestId: string): Promise<any[]> {
    const response = await fetch(`${this.API_BASE_URL}/contests/${contestId}/available-participants`, {
      ...this.getRequestOptions(),
    });

    if (!response.ok) {
      throw new Error('Failed to fetch available contest participants');
    }

    return response.json();
  }

  async addMultipleParticipantsToContest(contestId: string, participantIds: string[]): Promise<{
    message: string;
    successful: string[];
    failed: { participantId: string; reason: string; }[];
    summary: { total: number; successful: number; failed: number; };
  }> {
    const response = await fetch(`${this.API_BASE_URL}/contests/${contestId}/participants/bulk`, {
      method: 'POST',
      ...this.getRequestOptions(),
      body: JSON.stringify({ participantIds }),
    });

    if (!response.ok) {
      throw new Error('Failed to add multiple participants to contest');
    }

    return response.json();
  }

  async getGroupMembers(groupId: string, page: number = 1, limit: number = 100): Promise<{ members: any[], page: number, limit: number }> {
    const response = await fetch(`${this.API_BASE_URL}/groups/${groupId}/members?page=${page}&limit=${limit}`, {
      ...this.getRequestOptions(),
    });

    if (!response.ok) {
      throw new Error('Failed to fetch group members');
    }

    return response.json();
  }

  async getUserGroupRoleForContest(contestId: string): Promise<{ groupId: string, userRole: 'admin' | 'member' | null }> {
    // First get the contest to know which group it belongs to
    const contest = await this.getContest(contestId);
    
    // Then get the user's role in that group
    const response = await fetch(`${this.API_BASE_URL}/groups/${contest.groupId}/role`, {
      ...this.getRequestOptions(),
    });

    if (!response.ok) {
      throw new Error('Failed to fetch user role');
    }

    const roleData = await response.json();
    return {
      groupId: contest.groupId,
      userRole: roleData.role
    };
  }

  async getContestTeamLeaderboard(contestId: string, metric: string = 'totalDistance', limit: number = 1000): Promise<any> {
    const params = new URLSearchParams({
      metric,
      limit: limit.toString()
    });
    
    const response = await fetch(`${this.API_BASE_URL}/contests/${contestId}/team-leaderboard?${params}`, {
      ...this.getRequestOptions(),
    });
    
    if (!response.ok) {
      throw new Error('Failed to fetch contest team leaderboard');
    }
    
    return response.json();
  }


  async getTeamStats(teamId: string): Promise<any> {
    const response = await fetch(`${this.API_BASE_URL}/teams/${teamId}/stats`, {
      ...this.getRequestOptions(),
    });

    if (!response.ok) {
      throw new Error('Failed to fetch team stats');
    }

    return response.json();
  }

  async getTeamMemberLeaderboard(contestId: string, teamId?: string, limit: number = 100): Promise<any> {
    const params = new URLSearchParams({
      limit: limit.toString()
    });

    if (teamId) {
      params.append('teamId', teamId);
    }

    const response = await fetch(`${this.API_BASE_URL}/contests/${contestId}/team-member-leaderboard?${params}`, {
      ...this.getRequestOptions(),
    });

    if (!response.ok) {
      throw new Error('Failed to fetch team member leaderboard');
    }

    return response.json();
  }

  // Admin only. activityId is the contest activity record id; dismissed=false restores the flag.
  async reviewSuspiciousActivity(contestId: string, activityId: string, dismissed: boolean): Promise<void> {
    const response = await fetch(`${this.API_BASE_URL}/contests/${contestId}/activities/${activityId}/suspicious-review`, {
      method: 'PUT',
      ...this.getRequestOptions(),
      body: JSON.stringify({ dismissed }),
    });

    if (!response.ok) {
      const body = await response.json().catch(() => null);
      throw new Error(body?.message || 'Failed to review activity');
    }
  }

  async getContestTeamMemberActivities(contestId: string, userId: string): Promise<any> {
    const response = await fetch(`${this.API_BASE_URL}/contests/${contestId}/team-members/${userId}/activities`, {
      ...this.getRequestOptions(),
    });

    if (!response.ok) {
      throw new Error('Failed to fetch contest team member activities');
    }

    return response.json();
  }
}

export const apiService = new ApiService(); 